import { useMemo, useRef, useState } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import { Formik, FormikProps } from 'formik';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { CubeDefinitionRowItem } from 'types/api/SystemTenant/AROCube/CubeBuilderTypes';
import {
  createCalculationRuleParametersValidationSchema,
  createCubeFunctionRuleParametersValidationSchema,
} from 'config/FormValidaton/AroCube/CubeDefinitionSettingPanelValidationSchema';
import { RULES_CONSTANT } from 'constant/RuleConstant';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 620,
  maxHeight: '88vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export interface ModalCubeDefinitionParamsProps<T = CubeDefinitionRowItem> {
  visible?: boolean;
  onClose?: () => void;
  cubeDefinitionItem: CubeDefinitionRowItem | null;
  onSubmitParameter?: (parameterValue: T) => void;
  referenceCodes?: { code: string; label: string }[];
  fieldName: 'ruleTypeParams' | 'consolidateRuleParams' | null;
}

type FormValues = {
  parameter: string;
};

const DOCS_LINK = {
  [RULES_CONSTANT.CUBE.RULE_CUBE_CALC_ATTRIBUTE]: {
    href: 'https://staging.d36u5obghxsqcu.amplifyapp.com/docs/builder-cube-definition-guide/cube-calculate-attribute-guide',
    label: 'Cube Calculate Attribute Guide',
  },
  [RULES_CONSTANT.CUBE.RULE_CUBE_FUNCTION]: {
    href: 'https://staging.d36u5obghxsqcu.amplifyapp.com/docs/builder-cube-definition-guide/cube-functions-guide',
    label: 'Cube Functions Guide',
  },
};

const INPUT_TEXT_LABEL = {
  [RULES_CONSTANT.CUBE.RULE_CUBE_CALC_ATTRIBUTE]: {
    placeholder: 'e.g. A+B or (A-B)*C',
    label: 'Calculation Rule Expression',
  },
  [RULES_CONSTANT.CUBE.RULE_CUBE_FUNCTION]: {
    placeholder: 'e.g. A(REF) or AVG(REF,6)',
    label: 'Special Function Expression',
  },
};

const FLAG_ENABLE_EXPRESSION_AUTOCOMPLETE = false;

// Cube function definitions

type CubeFunctionVariant = { template: string; description: string; hint?: string };
type CubeFunctionDef = {
  name: string;
  label: string;
  description: string;
  template: string;
  variants?: CubeFunctionVariant[];
};

// INIT is added to every period, not just the first; users often misread it as an opening value
const SHIFT_INIT_HINT =
  "INIT is added to every period after the shift. For an opening value (first period only), point INIT at a row that only has a value in the first period: a 'Data Entry: Initial' row, or a helper row B(X) of a row that's flat across periods.\n" +
  'Example: Prior Year = S(C,1,H) with H = B(F), where F = Last Year Tonnes.';

const CUBE_FUNCTIONS: CubeFunctionDef[] = [
  {
    name: 'A',
    label: 'Accumulate',
    description: 'Running cumulative total or sliding window',
    template: 'A(REF)',
    variants: [
      { template: 'A(REF)', description: 'Cumulative running total from start' },
      { template: 'A(REF,TLn)', description: 'Total last n periods (sliding window)' },
      { template: 'A(REF,TL,REF2)', description: 'Total last dynamic (window size from REF2)' },
      { template: 'A(REF,ALn)', description: 'Average last n periods' },
      { template: 'A(REF,TNn)', description: 'Total next n periods (forward-looking)' },
      { template: 'A(REF,TN,REF2)', description: 'Total next dynamic (window size from REF2)' },
      { template: 'A(REF,ANn)', description: 'Average next n periods' },
    ],
  },
  {
    name: 'B',
    label: 'Balance Change',
    description: 'Period-over-period difference',
    template: 'B(REF)',
    variants: [
      { template: 'B(REF)', description: 'Current period minus previous period' },
      { template: 'B(REF)+X', description: 'Balance change then add attribute X' },
      { template: 'B(REF)-X', description: 'Balance change then subtract attribute X' },
    ],
  },
  {
    name: 'S',
    label: 'Shift',
    description: 'Shifts values forward or backward in time',
    template: 'S(REF)',
    variants: [
      { template: 'S(REF)', description: 'Shift forward by 1 period (default)' },
      { template: 'S(REF,n)', description: 'Shift by n periods (negative = backward)' },
      { template: 'S(REF,1.5)', description: 'Fractional shift, interpolates between periods' },
      { template: 'S(REF,LAG)', description: 'Dynamic shift, offset from LAG attribute' },
      { template: 'S(REF,-LAG)', description: 'Dynamic shift using the negated LAG value' },
      { template: 'S(REF,n,INIT)', description: 'Shift by n, then add INIT in every period', hint: SHIFT_INIT_HINT },
      { template: 'S(REF,LAG,INIT)', description: 'Dynamic shift, then add INIT in every period', hint: SHIFT_INIT_HINT },
      {
        template: 'S(REF,-LAG,INIT)',
        description: 'Dynamic shift with negated LAG, then add INIT in every period',
        hint: SHIFT_INIT_HINT,
      },
    ],
  },
  {
    name: 'P',
    label: 'Percentage',
    description: 'Each value as % of its group total',
    template: 'P(REF,n)',
  },
  {
    name: 'U',
    label: 'Utilisation',
    description: 'Max utilisation % across KM, engine hours, and trips',
    template: 'U(AKM,AEH,ATR,XKM,XEH,XTR)',
  },
  {
    name: 'SU',
    label: 'Simple Utilisation',
    description: 'Actual vs achievable %, capped at 100%',
    template: 'SU(actual,achievable)',
  },
  {
    name: 'H',
    label: 'High',
    description: 'Average of top n values in a 20-period lookback window',
    template: 'H(REF,n)',
  },
  {
    name: 'RT',
    label: 'Rolling Total',
    description: 'Sliding window sum, fills from the first period',
    template: 'RT(REF,w)',
  },
  {
    name: 'AVG',
    label: 'Average',
    description: 'Simple moving average over a sliding window',
    template: 'AVG(REF,w)',
  },
  {
    name: 'AVG_HIGH',
    label: 'Average High',
    description: 'Average of the top n values within a window',
    template: 'AVG_HIGH(REF,w,n)',
  },
  {
    name: 'AVG_LOW',
    label: 'Average Low',
    description: 'Average of the bottom n values within a window',
    template: 'AVG_LOW(REF,w,n)',
  },
];

const FUNC_LABEL_MAP: Record<string, string> = Object.fromEntries(CUBE_FUNCTIONS.map((fn) => [fn.name, fn.label]));

// Expression tokeniser: CALC_ATTRIBUTE (math formulas)

type CalcTokenType = 'field' | 'operator' | 'number' | 'paren' | 'unknown';

const CALC_TOKEN_STYLE: Record<CalcTokenType, { color: string; bg?: string; fontWeight?: string }> = {
  field:    { color: '#3B4797', bg: '#EEF2FF', fontWeight: '700' },
  number:   { color: '#047857', bg: '#ECFDF5' },
  operator: { color: '#6B7280' },
  paren:    { color: '#374151' },
  unknown:  { color: '#DC2626', bg: '#FEF2F2' },
};

function tokenizeCalcExpression(expr: string, codes: string[]): { value: string; type: CalcTokenType }[] {
  const result: { value: string; type: CalcTokenType }[] = [];
  const re = new RegExp(/([A-Z_]+|\d+(?:\.\d+)?|[+\-*()/])/.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(expr)) !== null) {
    if (match.index > lastIndex) result.push({ value: expr.slice(lastIndex, match.index), type: 'unknown' });
    const t = match[0];
    if (codes.includes(t)) result.push({ value: t, type: 'field' });
    else if (/^\d+(?:\.\d+)?$/.test(t)) result.push({ value: t, type: 'number' });
    else if (/^[+\-*/]$/.test(t)) result.push({ value: t, type: 'operator' });
    else if (/^[()]$/.test(t)) result.push({ value: t, type: 'paren' });
    else result.push({ value: t, type: 'unknown' });
    lastIndex = re.lastIndex;
  }
  if (lastIndex < expr.length) result.push({ value: expr.slice(lastIndex), type: 'unknown' });
  return result;
}

// Expression tokeniser: RULE_CUBE_FUNCTION (function call syntax)

type FuncTokenType = 'funcName' | 'field' | 'period' | 'number' | 'paren' | 'comma' | 'operator' | 'unknown';

const FUNC_TOKEN_STYLE: Record<FuncTokenType, { color: string; bg?: string; fontWeight?: string }> = {
  funcName: { color: '#5B21B6', bg: '#EDE9FE', fontWeight: '700' },
  field:    { color: '#3B4797', bg: '#EEF2FF', fontWeight: '700' },
  period:   { color: '#B45309', bg: '#FEF3C7' },
  number:   { color: '#047857', bg: '#ECFDF5' },
  paren:    { color: '#6B7280' },
  comma:    { color: '#6B7280' },
  operator: { color: '#6B7280' },
  unknown:  { color: '#DC2626', bg: '#FEF2F2' },
};

const FUNC_NAMES = new Set(['A', 'B', 'S', 'P', 'U', 'SU', 'H', 'RT', 'AVG', 'AVG_HIGH', 'AVG_LOW', 'R']);
const PERIOD_RE = /^(YTD|TL(\d+(\.\d+)?)?|AL\d+|TN(\d+(\.\d+)?)?|AN\d+)$/;

function tokenizeFuncExpression(expr: string, codes: string[]): { value: string; type: FuncTokenType }[] {
  const result: { value: string; type: FuncTokenType }[] = [];
  const re = new RegExp(/([A-Z][A-Z0-9_]*|\d+(?:\.\d+)?|[(),+\-*/])/.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let firstWord = true;

  while ((match = re.exec(expr)) !== null) {
    if (match.index > lastIndex) result.push({ value: expr.slice(lastIndex, match.index), type: 'unknown' });
    const t = match[0];

    if (t === ',') {
      result.push({ value: t, type: 'comma' });
    } else if (/^[()]$/.test(t)) {
      result.push({ value: t, type: 'paren' });
    } else if (/^[+\-*/]$/.test(t)) {
      result.push({ value: t, type: 'operator' });
    } else if (/^\d+(?:\.\d+)?$/.test(t)) {
      result.push({ value: t, type: 'number' });
    } else if (firstWord && FUNC_NAMES.has(t)) {
      result.push({ value: t, type: 'funcName' });
      firstWord = false;
    } else if (PERIOD_RE.test(t)) {
      result.push({ value: t, type: 'period' });
    } else if (codes.includes(t)) {
      result.push({ value: t, type: 'field' });
    } else {
      result.push({ value: t, type: 'unknown' });
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < expr.length) result.push({ value: expr.slice(lastIndex), type: 'unknown' });
  return result;
}

// Shared badge style for reference code and function name chips

const codeBadgeSx = {
  fontFamily: 'monospace',
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#3B4797',
  bgcolor: '#EEF2FF',
  border: '1px solid #C7D2FE',
  borderRadius: 0.75,
  px: 0.75,
  py: 0.15,
  flexShrink: 0,
  minWidth: 28,
  textAlign: 'center' as const,
  display: 'inline-block',
};

const funcBadgeSx = {
  ...codeBadgeSx,
  color: '#5B21B6',
  bgcolor: '#EDE9FE',
  border: '1px solid #DDD6FE',
};

// Component

export function ModalCubeDefinitionParameter(props: ModalCubeDefinitionParamsProps) {
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { onClose, visible = false, onSubmitParameter, referenceCodes, cubeDefinitionItem, fieldName } = props;

  const [acSuggestions, setAcSuggestions] = useState<string[]>([]);
  const [acAnchorEl, setAcAnchorEl] = useState<HTMLElement | null>(null);
  const [expandedFn, setExpandedFn] = useState<string | null>(null);

  const isConsolidate = fieldName === 'consolidateRuleParams';
  // @ts-ignore
  const isCalcAttribute = !isConsolidate && cubeDefinitionItem?.ruleType?.code === RULES_CONSTANT.CUBE.RULE_CUBE_CALC_ATTRIBUTE;
  // @ts-ignore
  const isCubeFunction = !isConsolidate && cubeDefinitionItem?.ruleType?.code === RULES_CONSTANT.CUBE.RULE_CUBE_FUNCTION;
  const showReferencePanel = (isCalcAttribute || isCubeFunction) && !!referenceCodes?.length;

  const codes = useMemo(() => referenceCodes?.map((r) => r.code) ?? [], [referenceCodes]);

  const handleOnCloseModal = () => onClose?.();
  const handleOnCancel = () => handleOnCloseModal();

  const handleOnSave = (data: FormValues) => {
    if (fieldName) {
      props.cubeDefinitionItem &&
        onSubmitParameter &&
        onSubmitParameter({ ...props.cubeDefinitionItem, [fieldName]: data.parameter });
      handleOnCloseModal();
    }
  };

  const closeAutocomplete = () => {
    setAcSuggestions([]);
    setAcAnchorEl(null);
  };

  const handleExpressionChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    const newValue = e.target.value.toUpperCase();
    setFieldValue('parameter', newValue);

    if (!FLAG_ENABLE_EXPRESSION_AUTOCOMPLETE || !isCalcAttribute || !codes.length) return;

    const cursor = (e.target as HTMLInputElement).selectionStart ?? newValue.length;
    let wordStart = cursor;
    while (wordStart > 0 && /[A-Z]/.test(newValue[wordStart - 1])) wordStart--;
    const partial = newValue.slice(wordStart, cursor);

    if (partial.length > 0) {
      const matches = codes.filter((c) => c.startsWith(partial));
      if (matches.length > 0) {
        setAcSuggestions(matches);
        setAcAnchorEl(inputRef.current);
        return;
      }
    }
    closeAutocomplete();
  };

  const insertFromAutocomplete = (
    code: string,
    currentValue: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    const input = inputRef.current;
    const cursor = input?.selectionStart ?? currentValue.length;
    let wordStart = cursor;
    while (wordStart > 0 && /[A-Z]/.test(currentValue[wordStart - 1])) wordStart--;
    const next = currentValue.slice(0, wordStart) + code + currentValue.slice(cursor);
    setFieldValue('parameter', next);
    closeAutocomplete();
    const pos = wordStart + code.length;
    setTimeout(() => { input?.focus(); input?.setSelectionRange(pos, pos); }, 0);
  };

  const insertAtCursor = (
    code: string,
    currentValue: string,
    setFieldValue: (field: string, value: string) => void,
  ) => {
    const input = inputRef.current;
    const cursor = input?.selectionStart ?? currentValue.length;
    const next = currentValue.slice(0, cursor) + code + currentValue.slice(cursor);
    setFieldValue('parameter', next);
    const pos = cursor + code.length;
    setTimeout(() => { input?.focus(); input?.setSelectionRange(pos, pos); }, 0);
  };

  const validationSchema = useMemo(() => {
    if (isConsolidate) return createCalculationRuleParametersValidationSchema(codes, isConsolidate);
    // @ts-ignore
    if (cubeDefinitionItem?.ruleType.code === RULES_CONSTANT.CUBE.RULE_CUBE_CALC_ATTRIBUTE)
      return createCalculationRuleParametersValidationSchema(codes, isConsolidate);
    return createCubeFunctionRuleParametersValidationSchema(codes);
  }, [codes, cubeDefinitionItem, isConsolidate]);

  const formikInitialValues = useMemo(() => {
    if (!fieldName) return { parameter: '' };
    return { parameter: props.cubeDefinitionItem?.[fieldName] || '' };
  }, [props.cubeDefinitionItem, fieldName]);

  // @ts-ignore
  const docsEntry = DOCS_LINK[cubeDefinitionItem?.ruleType?.code];

  return (
    <div>
      <Modal open={visible} onClose={handleOnCloseModal}>
        <Stack direction='column' sx={ModalContentStyle}>
          <Formik
            innerRef={formikRef}
            initialValues={formikInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleOnSave}
          >
            {({ handleSubmit, setFieldValue, handleBlur, touched, errors, values }) => {
              const codeLabelMap = Object.fromEntries((referenceCodes ?? []).map((r) => [r.code, r.label]));

              const calcTokens = isCalcAttribute && values.parameter
                ? tokenizeCalcExpression(values.parameter, codes) : [];
              const funcTokens = isCubeFunction && values.parameter
                ? tokenizeFuncExpression(values.parameter, codes) : [];

              const activeTokens = isCalcAttribute ? calcTokens : funcTokens;
              const hasUnknown = activeTokens.some((t) => t.type === 'unknown');
              const showPreview = (isCalcAttribute || isCubeFunction) && values.parameter.length > 0;

              return (
                <>
                  {/* Header */}
                  <Stack
                    px={2.5} py={1.5} direction='row' justifyContent='space-between' alignItems='center'
                    sx={{ borderBottom: '1px solid #E3EBF6', flexShrink: 0 }}
                  >
                    <Typography variant='subtitle1' fontWeight='bold' sx={{ color: '#3B4797' }}>
                      {isConsolidate
                        ? `Consolidate rule for ${props?.cubeDefinitionItem?.attribute?.label}`
                        : `Parameter for ${props?.cubeDefinitionItem?.attribute?.label}`}
                    </Typography>
                    <IconButton onClick={handleOnCloseModal} size='small'>
                      <HighlightOffRoundedIcon sx={closeIconStyle} />
                    </IconButton>
                  </Stack>

                  {/* Rule type badge */}
                  {!isConsolidate && (
                    <Stack
                      px={2.5} py={1.25} direction='row' alignItems='center'
                      sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #E3EBF6', flexShrink: 0 }}
                    >
                      <Typography variant='caption' color='text.secondary' sx={{ mr: 1 }}>
                        Rule type:
                      </Typography>
                      <Typography
                        variant='caption' fontWeight='bold'
                        sx={{ color: '#3B4797', bgcolor: '#EEF2FF', px: 1, py: 0.25, borderRadius: 1 }}
                      >
                        {cubeDefinitionItem?.ruleType?.label}
                      </Typography>
                    </Stack>
                  )}

                  {/* Scrollable body */}
                  <Stack px={2.5} py={2.5} spacing={2} sx={{ overflowY: 'auto', flex: 1 }}>

                    {/* Docs callout */}
                    {docsEntry && (
                      <Stack
                        direction='row' spacing={1} alignItems='flex-start'
                        sx={{ bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 1.5, px: 2, py: 1.25 }}
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: '#3B4797', mt: 0.25, flexShrink: 0 }} />
                        <Typography variant='caption' color='text.secondary' lineHeight={1.6}>
                          Refer to the{' '}
                          <Link
                            href={docsEntry.href} target='_blank' rel='noopener noreferrer'
                            sx={{
                              fontWeight: 600, color: '#3B4797',
                              display: 'inline-flex', alignItems: 'center', gap: 0.25,
                              textDecorationColor: '#3B4797',
                            }}
                          >
                            {docsEntry.label}
                            <OpenInNewRoundedIcon sx={{ fontSize: 11 }} />
                          </Link>{' '}
                          for documentation on how to fill this parameter.
                        </Typography>
                      </Stack>
                    )}

                    {/* Function picker: RULE_CUBE_FUNCTION only */}
                    {isCubeFunction && (
                      <Stack spacing={0.75}>
                        <Typography variant='caption' color='text.secondary' fontWeight={500}>
                          Available functions, click a row to insert:
                        </Typography>
                        <Stack
                          sx={{
                            border: '1px solid #E3EBF6',
                            borderRadius: 1,
                            overflow: 'hidden',
                            maxHeight: 240,
                            overflowY: 'auto',
                          }}
                        >
                          {CUBE_FUNCTIONS.map((fn, idx) => (
                            <Stack key={fn.name}>
                              {/* Main row */}
                              <Tooltip title={`Use: ${fn.template}`} placement='left' arrow>
                                <Stack
                                  direction='row' alignItems='center' spacing={1.25}
                                  onClick={() => { setFieldValue('parameter', fn.template); setTimeout(() => inputRef.current?.focus(), 0); }}
                                  sx={{
                                    px: 1.5, py: 0.75,
                                    cursor: 'pointer',
                                    bgcolor: idx % 2 === 0 ? '#FAFBFF' : '#FFFFFF',
                                    borderBottom: '1px solid #F0F4FF',
                                    '&:hover': { bgcolor: '#EDE9FE' },
                                  }}
                                >
                                  <Typography component='span' sx={funcBadgeSx}>
                                    {fn.name}
                                  </Typography>
                                  <Typography variant='caption' fontWeight={600} sx={{ color: '#374151', minWidth: 88, flexShrink: 0 }}>
                                    {fn.label}
                                  </Typography>
                                  <Typography
                                    component='span'
                                    sx={{
                                      fontFamily: 'monospace', fontSize: '0.7rem',
                                      color: '#5B21B6', bgcolor: '#F5F3FF',
                                      border: '1px solid #EDE9FE',
                                      px: 0.75, py: 0.15, borderRadius: 0.5,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {fn.template}
                                  </Typography>
                                  <Typography variant='caption' color='text.secondary' sx={{ flex: 1, fontSize: '0.7rem' }}>
                                    {fn.description}
                                  </Typography>
                                  {fn.variants && (
                                    <IconButton
                                      size='small'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedFn(expandedFn === fn.name ? null : fn.name);
                                      }}
                                      sx={{ p: 0.25, flexShrink: 0 }}
                                    >
                                      {expandedFn === fn.name
                                        ? <ExpandMoreRoundedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                        : <ChevronRightRoundedIcon sx={{ fontSize: 16, color: '#6B7280' }} />
                                      }
                                    </IconButton>
                                  )}
                                </Stack>
                              </Tooltip>

                              {/* Variant sub-rows */}
                              {fn.variants && (
                                <Collapse in={expandedFn === fn.name}>
                                  {fn.variants.map((v) => (
                                    <Tooltip
                                      key={v.template}
                                      title={v.hint ? <span style={{ whiteSpace: 'pre-line' }}>{v.hint}</span> : `Use: ${v.template}`}
                                      placement='left'
                                      arrow
                                    >
                                      <Stack
                                        direction='row' alignItems='center' spacing={1.5}
                                        onClick={() => { setFieldValue('parameter', v.template); setTimeout(() => inputRef.current?.focus(), 0); }}
                                        sx={{
                                          pl: 4.5, pr: 1.5, py: 0.65,
                                          cursor: 'pointer',
                                          bgcolor: '#F5F3FF',
                                          borderBottom: '1px solid #EDE9FE',
                                          '&:hover': { bgcolor: '#EDE9FE' },
                                        }}
                                      >
                                        <Typography
                                          component='span'
                                          sx={{
                                            fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 600,
                                            color: '#5B21B6', flexShrink: 0, minWidth: 150,
                                          }}
                                        >
                                          {v.template}
                                        </Typography>
                                        <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
                                          {v.description}
                                        </Typography>
                                      </Stack>
                                    </Tooltip>
                                  ))}
                                </Collapse>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {/* Reference codes panel: both CALC_ATTRIBUTE and RULE_CUBE_FUNCTION */}
                    {showReferencePanel && (
                      <Stack spacing={0.75}>
                        <Typography variant='caption' color='text.secondary' fontWeight={500}>
                          Available fields, click to insert at cursor:
                        </Typography>
                        <Stack direction='row' flexWrap='wrap' gap={0.75}>
                          {referenceCodes!.map((item) => (
                            <Tooltip key={item.code} title={`Insert field "${item.code}"`} placement='top' arrow>
                              <Stack
                                direction='row' alignItems='center' spacing={0.6}
                                onClick={() => insertAtCursor(item.code, values.parameter, setFieldValue)}
                                sx={{
                                  border: '1px solid #C7D2FE',
                                  borderRadius: 10,
                                  px: 1, py: 0.35,
                                  cursor: 'pointer',
                                  bgcolor: '#F5F7FF',
                                  userSelect: 'none',
                                  '&:hover': {
                                    bgcolor: '#EEF2FF',
                                    borderColor: '#818CF8',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 6px rgba(59,71,151,0.15)',
                                  },
                                  transition: 'all 0.12s ease',
                                }}
                              >
                                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 700, color: '#3B4797' }}>
                                  {item.code}
                                </Typography>
                                {item.label !== item.code && (
                                  <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>
                                    {item.label}
                                  </Typography>
                                )}
                              </Stack>
                            </Tooltip>
                          ))}
                        </Stack>
                      </Stack>
                    )}

                    {/* Input */}
                    <TextField
                      inputRef={inputRef}
                      value={values.parameter}
                      name='parameter'
                      onChange={(e) => handleExpressionChange(e, setFieldValue)}
                      onFocus={() => {
                        if (FLAG_ENABLE_EXPRESSION_AUTOCOMPLETE && isCalcAttribute && codes.length && !values.parameter) {
                          setAcSuggestions(codes);
                          setAcAnchorEl(inputRef.current);
                        }
                      }}
                      onBlur={handleBlur}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' && acSuggestions.length > 0) {
                          e.stopPropagation();
                          closeAutocomplete();
                        }
                      }}
                      // @ts-ignore
                      label={INPUT_TEXT_LABEL[cubeDefinitionItem?.ruleType?.code]?.label || 'Calculation Rule'}
                      fullWidth size='small'
                      // @ts-ignore
                      placeholder={INPUT_TEXT_LABEL[cubeDefinitionItem?.ruleType?.code]?.placeholder || 'Input rule'}
                      type='text'
                      error={touched.parameter && Boolean(errors.parameter)}
                      helperText={touched.parameter && errors.parameter}
                      inputProps={{ autoComplete: 'off' }}
                      InputProps={{
                        sx: { fontFamily: 'monospace', letterSpacing: '0.03em' },
                        endAdornment: values.parameter ? (
                          <InputAdornment position='end'>
                            <IconButton
                              size='small'
                              onClick={() => { setFieldValue('parameter', ''); closeAutocomplete(); }}
                              edge='end'
                              sx={{ color: '#9CA3AF', '&:hover': { color: '#6B7280' } }}
                            >
                              <ClearRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />

                    {/* Autocomplete popover */}
                    <Popover
                      open={acSuggestions.length > 0 && Boolean(acAnchorEl)}
                      anchorEl={acAnchorEl}
                      onClose={closeAutocomplete}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      disableAutoFocus disableEnforceFocus
                      slotProps={{ paper: { sx: { maxHeight: 220, overflowY: 'auto', width: inputRef.current?.offsetWidth } } }}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuList dense sx={{ py: 0.5, minWidth: 200 }}>
                        {acSuggestions.map((code) => (
                          <MenuItem
                            key={code}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertFromAutocomplete(code, values.parameter, setFieldValue)}
                            sx={{ gap: 1.5 }}
                          >
                            <Typography component='span' sx={codeBadgeSx}>{code}</Typography>
                            <Typography variant='caption' color='text.secondary' noWrap>
                              {codeLabelMap[code] ?? code}
                            </Typography>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Popover>

                    {/* Live expression preview */}
                    {showPreview && (
                      <Stack spacing={0.5}>
                        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={0.5}>
                          <Typography variant='caption' color='text.secondary' fontWeight={500}>
                            Expression preview:
                          </Typography>
                          <Stack direction='row' flexWrap='wrap' gap={0.5} alignItems='center'>
                            {isCalcAttribute && [
                              { label: 'field', color: '#3B4797', bg: '#EEF2FF' },
                              { label: 'number', color: '#047857', bg: '#ECFDF5' },
                              { label: 'operator', color: '#6B7280' },
                              { label: 'unknown', color: '#DC2626', bg: '#FEF2F2' },
                            ].map((s) => (
                              <Typography
                                key={s.label}
                                sx={{ fontSize: '0.62rem', color: s.color, bgcolor: s.bg, px: 0.6, py: 0.1, borderRadius: 0.5, fontFamily: 'monospace' }}
                              >
                                {s.label}
                              </Typography>
                            ))}
                            {isCubeFunction && [
                              { label: 'function', color: '#5B21B6', bg: '#EDE9FE' },
                              { label: 'field', color: '#3B4797', bg: '#EEF2FF' },
                              { label: 'period', color: '#B45309', bg: '#FEF3C7' },
                              { label: 'number', color: '#047857', bg: '#ECFDF5' },
                              { label: 'unknown', color: '#DC2626', bg: '#FEF2F2' },
                            ].map((s) => (
                              <Typography
                                key={s.label}
                                sx={{ fontSize: '0.62rem', color: s.color, bgcolor: s.bg, px: 0.6, py: 0.1, borderRadius: 0.5, fontFamily: 'monospace' }}
                              >
                                {s.label}
                              </Typography>
                            ))}
                          </Stack>
                        </Stack>
                        <Stack
                          direction='row' flexWrap='wrap' alignItems='center'
                          sx={{
                            bgcolor: hasUnknown ? '#FFF9F9' : '#F0FDF4',
                            border: `1px solid ${hasUnknown ? '#FECACA' : '#BBF7D0'}`,
                            borderRadius: 1,
                            px: 1.5, py: 0.75,
                            minHeight: 34,
                            gap: 0.25,
                          }}
                        >
                          {isCalcAttribute && calcTokens.map((token, i) => (
                            <Typography
                              key={i} component='span'
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                                fontWeight: CALC_TOKEN_STYLE[token.type as CalcTokenType].fontWeight || 'normal',
                                color: CALC_TOKEN_STYLE[token.type as CalcTokenType].color,
                                bgcolor: CALC_TOKEN_STYLE[token.type as CalcTokenType].bg,
                                px: CALC_TOKEN_STYLE[token.type as CalcTokenType].bg ? 0.6 : 0.25,
                                borderRadius: 0.5,
                                lineHeight: 1.8,
                              }}
                            >
                              {token.type === 'field' ? (codeLabelMap[token.value] ?? token.value) : token.value}
                            </Typography>
                          ))}
                          {isCubeFunction && funcTokens.map((token, i) => {
                            const style = FUNC_TOKEN_STYLE[token.type as FuncTokenType];
                            return (
                              <Typography
                                key={i} component='span'
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.82rem',
                                  fontWeight: style.fontWeight || 'normal',
                                  color: style.color,
                                  bgcolor: style.bg,
                                  px: style.bg ? 0.6 : 0.25,
                                  borderRadius: 0.5,
                                  lineHeight: 1.8,
                                }}
                              >
                                {token.type === 'field'
                                  ? (codeLabelMap[token.value] ?? token.value)
                                  : token.type === 'funcName'
                                    ? (FUNC_LABEL_MAP[token.value] ?? token.value)
                                    : token.value}
                              </Typography>
                            );
                          })}
                        </Stack>
                        {hasUnknown ? (
                          <Typography variant='caption' sx={{ color: '#DC2626' }}>
                            Red tokens are unrecognised. Check spelling or pick a field from the list above.
                          </Typography>
                        ) : (
                          <Typography variant='caption' sx={{ color: '#15803D' }}>
                            Expression looks valid.
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>

                  {/* Footer */}
                  <Stack px={2.5} pb={2} sx={{ borderTop: '1px solid #E3EBF6', flexShrink: 0 }}>
                    <Stack direction='row' justifyContent='flex-end' spacing={1.5} alignItems='center' pt={2}>
                      <Button variant='main-table-panel-border' onClick={handleOnCancel}>Cancel</Button>
                      <ButtonLoading variant='main-table-panel' onClick={() => handleSubmit()}>
                        Save Parameter
                      </ButtonLoading>
                    </Stack>
                  </Stack>
                </>
              );
            }}
          </Formik>
        </Stack>
      </Modal>
    </div>
  );
}
